export const DEFAULT_INACTIVITY_TIMEOUT_MINUTES = 15;
export const IDLE_WARNING_LEAD_MS = 2 * 60 * 1000;
export const USER_ACTIVITY_THROTTLE_MS = 1000;

/** DOM events that count as a person using the workstation. */
export const USER_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

export const LAST_ACTIVITY_STORAGE_KEY = 'session_last_activity_at';

export type UserActivityEvent = (typeof USER_ACTIVITY_EVENTS)[number];

/**
 * Background requests, including POST /auth/refresh, are not user activity.
 * Only the events in USER_ACTIVITY_EVENTS extend the idle session.
 */
export const isUserIdleActivity = (source: string): source is UserActivityEvent =>
    (USER_ACTIVITY_EVENTS as readonly string[]).includes(source);

export const resolveInactivityTimeoutMs = (minutes: unknown): number => {
    if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes <= 0) {
        return DEFAULT_INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
    }
    return minutes * 60 * 1000;
};

export const idleWarningLeadMs = (timeoutMs: number): number => {
    if (timeoutMs <= IDLE_WARNING_LEAD_MS) {
        return Math.floor(timeoutMs / 2);
    }
    return IDLE_WARNING_LEAD_MS;
};

export const msUntilIdleLogout = (lastActivityAt: number, now: number, timeoutMs: number): number =>
    Math.max(0, timeoutMs - (now - lastActivityAt));

export const msUntilIdleWarning = (lastActivityAt: number, now: number, timeoutMs: number): number =>
    Math.max(0, msUntilIdleLogout(lastActivityAt, now, timeoutMs) - idleWarningLeadMs(timeoutMs));

export const formatIdleWarningLead = (leadMs: number): string => {
    const minutes = Math.max(1, Math.round(leadMs / 60000));
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
};

export const readSharedLastActivityAt = (): number | null => {
    try {
        const raw = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

export const writeSharedLastActivityAt = (timestamp: number): void => {
    try {
        localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(timestamp));
    } catch {
        // The in-memory clock still runs when storage is unavailable.
    }
};

export const clearSharedLastActivityAt = (): void => {
    try {
        localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    } catch {
        // Ignoring storage failures still allows this tab to sign out.
    }
};
